import React, { useCallback, useEffect, useRef } from 'react';
import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { isEmpty } from 'lodash/fp';
import { NoContent, Table, TablePaginationWithCount } from '../../../../ui-kit';
import { useExistingSuppliersQuery, usePaginatedDeliveryListsQuery } from '../../../../generated/graphql';
import { useWarehousesScope } from '../../../warehouse/providers/WarehouseScopeProvider';
import DeliveryListTableHeadCell from './DeliveryListsTableHeadCell';
import { deliveryListsHeadCells } from './constants';
import DeliveryListsTableContent from './DeliveryListsTableContent';
import useSearchParams from './useSearchParams';

const DeliveryListsTable = () => {
    const { t } = useTranslation();
    const noDeliveryListTitle = t('No delivery list created');
    const { currentWarehouseId } = useWarehousesScope();
    const { isExact } = useRouteMatch();

    const {
        deliveryDateUtc, setDeliveryDateUtc,
        supplierId, setSupplierId,
        invoiceNumber, setInvoiceNumber,
        page, setPage,
        rowsPerPage, setRowsPerPage,
    } = useSearchParams();
    const offset = page * rowsPerPage;

    const {
        data: existingSuppliersQuery,
        refetch: refetchSuppliers,
    } = useExistingSuppliersQuery();

    const { data, loading, refetch: refetchDeliveryLists } = usePaginatedDeliveryListsQuery(
        {
            variables: {
                offset,
                limit: rowsPerPage,
                warehouseId: currentWarehouseId as string,
                deliveryDateUtc,
                supplierId,
                invoiceNumber,
            },
            fetchPolicy: 'cache-and-network'
        },
    );

    const refetchQueries = useCallback(
        async () => {
            const suppliersPromise = refetchSuppliers();
            const deliveryListsPromise = refetchDeliveryLists();
            await Promise.all([suppliersPromise, deliveryListsPromise]);
        },
        // eslint-disable-next-line
        [],
    );

    /*
        Following logic ensures we refetch table after closing update/create form
        1. isExact = true
        2. isExact = false
        3. isExact = true
        So if previous isExact is false we just returned from the update/create form
    */
    const prevIsExact = useRef(isExact);
    useEffect(() => {
        if (!prevIsExact.current) {
            // eslint-disable-next-line no-void
            void refetchQueries();

        }
        prevIsExact.current = isExact;
    // eslint-disable-next-line
    }, [isExact]);

    useEffect(() => {
        setPage(0);
    // eslint-disable-next-line
    }, [deliveryDateUtc, supplierId, invoiceNumber, rowsPerPage]);

    const tablePagination: TablePaginationWithCount = {
        onChangePage: (_, pageNumber) => setPage(pageNumber),
        currentPage: page,
        onChangeRowsPerPage: e => setRowsPerPage(parseInt(e.target.value, 10)),
        rowsPerPage,
        count: data?.paginatedDeliveryLists.count || 0,
    };

    const paginatedDeliveryLists = data ? data.paginatedDeliveryLists.items : [];

    const noFilters = [deliveryDateUtc, supplierId, invoiceNumber]
        .every(arg => arg === null || arg === '');

    const noContent = data && noFilters ? data.paginatedDeliveryLists.count === 0 : false;
    return noContent
        ? <NoContent title={noDeliveryListTitle} />
        : (
            <Grid container justify="center" alignItems="center">
                <Grid item xs={12}>
                    <Table
                        isFirstColumnSticky
                        headCells={deliveryListsHeadCells}
                        customHeadCellProps={{
                            suppliers: existingSuppliersQuery ? existingSuppliersQuery.existingSuppliers : [],
                            deliveryDateUtc, setDeliveryDateUtc,
                            supplierId, setSupplierId,
                            invoiceNumber, setInvoiceNumber,
                        }}
                        TableHeadCellComponent={DeliveryListTableHeadCell}
                        tablePagination={tablePagination}
                    >
                        <DeliveryListsTableContent
                            isLoading={loading && isEmpty(paginatedDeliveryLists)}
                            rowsPerPage={rowsPerPage}
                            deliveryLists={paginatedDeliveryLists}
                        />
                    </Table>
                </Grid>
            </Grid>
        );
};

export default DeliveryListsTable;
